import { useState, useEffect } from 'react';
import { Col, Row } from 'antd';
import VolumeControls from './Volume';
import { Tooltip } from '../../../Tooltip';
import { FullScreenPlayer } from '../../../FullScreen';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';

// Icons
import {
  DetailsIcon,
  DeviceIcon,
  ExpandIcon,
  ListIcon,
  MicrophoneIcon,
  PhoneIcon,
} from '../../../Icons';

// I18n
import { useTranslation } from 'react-i18next';

// Redux
import { uiActions } from '../../../../store/slices/ui';
import { languageActions } from '../../../../store/slices/language';
import { useAppDispatch, useAppSelector } from '../../../../store/store';
import { useLocation, useNavigate } from 'react-router-dom';
import { miniPlayerManager } from '../../../MiniPlayer/miniPlayerManager';

export const LyricsButton = () => {
  const { t } = useTranslation(['playingBar']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isLyrics = location.pathname === '/lyrics';

  return (
    <Tooltip title={t('Lyrics')}>
      <button
        style={{ marginLeft: 5, marginRight: 5 }}
        className={isLyrics ? 'active-icon-button' : ''}
        onClick={() => {
          dispatch(uiActions.closeFullscreenNowPlaying());
          
          if (isLyrics) {
            navigate(-1);
          } else {
            navigate('/lyrics');
          }
        }}
      >
        <MicrophoneIcon />
      </button>
    </Tooltip>
  );
};

export const DetailsButton = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['playingBar']);
  const [hovered, setHovered] = useState(false);

  const active = useAppSelector((state) => !state.ui.detailsCollapsed);

  return (
    <>
      <Tooltip title={t('Now playing view')}>
        <button
          className={active ? 'active-icon-button tablet-hidden' : 'tablet-hidden'}
          onClick={() => dispatch(uiActions.toggleDetails())}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{
            marginLeft: 5,
            marginRight: 10,
            cursor: 'pointer',
          }}
        >
          <DetailsIcon active={active} hovered={hovered} />
        </button>
      </Tooltip>
    </>
  );
};

export const QueueButton = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['playingBar']);
  const queueCollapsed = useAppSelector((state) => state.ui.queueCollapsed);
  return (
    <Tooltip title={t('Queue')}>
      <button
        onClick={() => dispatch(uiActions.toggleQueue())}
        className={!queueCollapsed ? 'active-icon-button' : ''}
        style={{
          marginLeft: 10,
          marginRight: 5,
          cursor: queueCollapsed ? 'pointer' : 'not-allowed',
        }}
      >
        <ListIcon active={!queueCollapsed} />
      </button>
    </Tooltip>
  );
};

const ExpandButton = () => {
  const { t } = useTranslation(['playingBar']);

  const handle = useFullScreenHandle();
  const isQueueOpen = useAppSelector((state) => !state.ui.queueCollapsed);

  return (
    <>
      <FullScreen handle={handle}>
        <FullScreenPlayer onExit={handle.exit} />
      </FullScreen>

      <Tooltip title={t('Full Screen')}>
        <button
          className='tablet-hidden'
          onClick={handle.enter}
          style={{
            marginRight: 5,
            cursor: isQueueOpen ? 'pointer' : 'not-allowed',
          }}
        >
          <ExpandIcon />
        </button>
      </Tooltip>
    </>
  );
};

export const DeviceButton = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['playingBar']);
  const isDeviceOpen = useAppSelector((state) => !state.ui.devicesCollapsed);

  const currentDevice = useAppSelector((state) => state.spotify.activeDeviceType);

  return (
    <Tooltip title={t('Connect to a device')}>
      <button
        onClick={() => dispatch(uiActions.toggleDevices())}
        className={isDeviceOpen ? 'active-icon-button' : ''}
        style={{ marginTop: 4, cursor: isDeviceOpen ? 'pointer' : 'not-allowed' }}
      >
        {currentDevice === 'Smartphone' ? (
          <PhoneIcon active={isDeviceOpen} />
        ) : (
          <DeviceIcon active={isDeviceOpen} />
        )}
      </button>
    </Tooltip>
  );
};

export const MiniPlayerButton = () => {
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector((state) => state.ui.miniplayerOpen);

  const handleToggle = async () => {
    if (isOpen || miniPlayerManager.isOpen) {
      miniPlayerManager.close();
      dispatch(uiActions.closeMiniplayer());
    } else {
      const win = await miniPlayerManager.openWindow(() => {
        dispatch(uiActions.closeMiniplayer());
      });
      if (win) {
        dispatch(uiActions.openMiniplayer());
      }
    }
  };

  return (
    <Tooltip title={isOpen ? 'Close Miniplayer' : 'Open Miniplayer'}>
      <button
        onClick={handleToggle}
        className={isOpen ? 'active-icon-button' : ''}
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          padding: '2px 4px',
          background: 'none',
          border: 'none',
          color: isOpen ? '#1ed760' : 'rgba(255, 255, 255, 0.7)',
        }}
      >
        <svg role='img' height='16' width='16' viewBox='0 0 16 16' fill='none'>
          <rect
            x='1.5'
            y='2.5'
            width='13'
            height='10.5'
            rx='1.5'
            stroke={isOpen ? '#1ed760' : 'currentColor'}
            strokeWidth='1.5'
          />
          <rect
            x='8'
            y='7'
            width='5.5'
            height='5'
            rx='0.5'
            fill={isOpen ? '#1ed760' : 'currentColor'}
          />
        </svg>
        {isOpen && (
          <div
            style={{
              position: 'absolute',
              bottom: -6,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 4,
              height: 4,
              borderRadius: '50%',
              backgroundColor: '#1ed760',
            }}
          />
        )}
      </button>
    </Tooltip>
  );
};

const ExtraControlButtons = () => {
  return (
    <div>
      <Row gutter={18} align='middle'>
        <LyricsButton />

        <QueueButton />

        <Col className='hiddable-icon'>
          <DeviceButton />
        </Col>

        <Col>
          <VolumeControls />
        </Col>

        <Col className='tablet-hidden'>
          <MiniPlayerButton />
        </Col>

        <Col>
          <ExpandButton />
        </Col>
      </Row>
    </div>
  );
};

export default ExtraControlButtons;
